import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import FocusLock from 'react-focus-lock';
import Button from '@atlaskit/button';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { JiraFieldType } from '../../../../../common/constants/fields';
import type { ColumnState } from '../../../../../common/types';
import { FieldSelectorItem } from '../../../../../common/ui/field-selector-item';
import { getDescriptorForNewFieldType, getIcon } from '../../../../../common/utils/fields';
import { useMappingState } from '../../../../../controllers/import-state';
import messages from './messages';

export type Props = {
	fieldMap: ColumnState;
	onBack: () => void;
};

export const NewCustomFields = ({ fieldMap, onBack }: Props) => {
	const { formatMessage } = useIntl();
	const [mappingState, { setFieldForColumn }] = useMappingState();

	const onItemClick = useCallback(
		(fieldType: JiraFieldType) => {
			setFieldForColumn(fieldMap.columnName, {
				fieldId: null,
				fieldName: fieldMap.columnName,
				fieldType,
			});
			onBack();
		},
		[fieldMap.columnName, onBack, setFieldForColumn],
	);

	const availableCustomFieldTypeItems = useMemo(
		() =>
			mappingState?.availableNewFieldTypes?.map((type: string) => {
				const isSelected = fieldMap.field?.fieldId === null && fieldMap.field?.fieldType === type;
				return (
					<FieldSelectorItem
						key={type}
						name={formatMessage(getDescriptorForNewFieldType(type))}
						icon={getIcon({
							fieldType: type,
							isSelected,
						})}
						onClick={() => onItemClick(type)}
						isDisabled={false}
						isSelected={isSelected}
					/>
				);
			}),
		[
			fieldMap.field?.fieldId,
			fieldMap.field?.fieldType,
			formatMessage,
			mappingState?.availableNewFieldTypes,
			onItemClick,
		],
	);

	return (
		<FocusLock autoFocus>
			<Box
				xcss={wrapperStyles}
				testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.new-custom-fields.dropdown-item-group"
			>
				<Box xcss={backStyles}>
					<RoundButton
						iconBefore={
							<ChevronLeftIcon
								label={formatMessage(messages.goBack)}
								size="large"
								primaryColor={token('color.icon.subtle', colors.N300)}
							/>
						}
						aria-label={formatMessage(messages.createNewCustomField)}
						appearance="subtle"
						spacing="none"
						onClick={onBack}
					/>
					<Box xcss={backHeadingStyles}>{formatMessage(messages.createNewCustomField)}</Box>
				</Box>
				<Box>
					<Box xcss={headingStyles}>{formatMessage(messages.selectCustomFieldType)}</Box>
				</Box>
				<DropdownItemGroup>{availableCustomFieldTypeItems}</DropdownItemGroup>
			</Box>
		</FocusLock>
	);
};

const wrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '47vh',
	width: '300px',
	paddingBlockEnd: 'space.100',
});

const headingStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
	fontWeight: token('font.weight.bold', '700'),
	lineHeight: '16px',
	marginInline: 'space.200',
});

const backStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingInline: 'space.100',
	paddingBlockStart: 'space.200',
	paddingBlockEnd: 'space.150',
});

const backHeadingStyles = xcss({
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.bold'),
	color: 'color.text.subtle',
	alignItems: 'center',
	paddingInline: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const RoundButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	borderRadius: '50% !important',
});
